import { getDomainsApi } from '@/api-calls/domains/get'
import {
  createDomainApi,
  deleteDomainApi,
  resetPrimaryDomainApi,
  setPrimaryDomainApi,
  verifyDomainApi,
} from '@/api-calls/domains/mutate'
import { ContainerWithSidebarAndNoTopbar } from '@/components/header/container'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Input,
  Label,
  Skeleton,
} from '@blogging/ui'
import {
  ApiError,
  useFetch,
  useMutation,
  useQueryClient,
} from '@ngtrvu/use-api'
import {
  AlertCircle,
  CheckCircle2,
  Copy,
  Loader2,
  MoreHorizontal,
  Plus,
  RefreshCw,
  Star,
  Trash2,
} from 'lucide-react'
import { SLUG_DOMAIN, useSite } from '@/hooks/use-site'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'

interface DnsRecord {
  type: string
  name: string
  value: string
  purpose?: 'routing' | 'ssl' | 'ownership'
  verified?: boolean
}

interface DomainVerification {
  id?: string | null
  status?: string | null
  sslStatus?: string | null
  records?: DnsRecord[]
}

type TlsMode = 'external' | 'managed'

const DEFAULT_SUBDOMAIN_ID = 'default'

interface Domain {
  id: string
  domain: string
  domainVerified: boolean
  isPrimary: boolean
  createdAt: string
  tlsMode?: TlsMode
  verification: DomainVerification | null
}

export function SiteSettingsDomainsPage() {
  const { siteId } = useParams()
  const {
    site,
    siteDomain,
    isLoading: isSiteLoading,
    refresh: refreshSite,
  } = useSite()
  const queryClient = useQueryClient()
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const [newTlsMode, setNewTlsMode] = useState<TlsMode>('external')
  // Local radio choice; null means "no unsaved change" and the checked radio
  // falls back to what the database says.
  const [selectedPrimaryId, setSelectedPrimaryId] = useState<string | null>(
    null,
  )

  const {
    data: domains,
    isLoading,
    error,
  } = useFetch(getDomainsApi, {
    resourceName: 'domains',
    initialParams: { siteId: siteId as string },
  })

  const domainList = (domains as Domain[] | undefined) ?? []

  // The primary domain as saved in the database. The site record's `domain`
  // is what actually serves the site, so it is the source of truth; fall back
  // to the domain rows' isPrimary flag if no row matches it.
  const currentPrimaryId = site?.domain
    ? (domainList.find((d) => d.domain === site.domain)?.id ??
      domainList.find((d) => d.isPrimary)?.id ??
      DEFAULT_SUBDOMAIN_ID)
    : DEFAULT_SUBDOMAIN_ID

  const invalidate = () =>
    queryClient.invalidateQueries({
      queryKey: [getDomainsApi.apiName, { siteId }],
    })

  const onMutationError = (fallback: string) => (error: any) => {
    const message =
      (error as ApiError)?.data?.error?.message ??
      (error as ApiError)?.data?.error
    toast.error((typeof message === 'string' && message) || fallback)
  }

  const { mutate: createDomain, isPending: isCreating } = useMutation(
    createDomainApi,
    {
      resourceName: 'domain',
      onSuccess: () => {
        invalidate()
        toast.success('Domain added')
        setIsAddDialogOpen(false)
        setNewDomain('')
        setNewTlsMode('external')
      },
      onError: onMutationError('Failed to add domain'),
    },
  )

  const { mutate: deleteDomain, isPending: isDeleting } = useMutation(
    deleteDomainApi,
    {
      resourceName: 'domain',
      onSuccess: () => {
        invalidate()
        toast.success('Domain deleted')
      },
      onError: onMutationError('Failed to delete domain'),
    },
  )

  const { mutate: verifyDomain, isPending: isVerifying } = useMutation(
    verifyDomainApi,
    {
      resourceName: 'domain',
      onSuccess: () => {
        invalidate()
        toast.success('Domain verified')
      },
      onError: onMutationError('Domain not verified yet'),
    },
  )

  const { mutate: setPrimaryDomain, isPending: isSettingPrimary } = useMutation(
    setPrimaryDomainApi,
    {
      resourceName: 'domain',
      onSuccess: () => {
        invalidate()
        refreshSite()
        setSelectedPrimaryId(null)
        toast.success('Primary domain updated')
      },
      onError: onMutationError('Failed to set primary domain'),
    },
  )

  const { mutate: resetPrimaryDomain, isPending: isResetting } = useMutation(
    resetPrimaryDomainApi,
    {
      resourceName: 'domain',
      onSuccess: () => {
        invalidate()
        refreshSite()
        setSelectedPrimaryId(null)
        toast.success('Primary domain reset to subdomain')
      },
      onError: onMutationError('Failed to reset primary domain'),
    },
  )

  const handleCopy = (value: string) => {
    navigator.clipboard.writeText(value)
    toast.success('Copied')
  }

  const handleAddDomain = () => {
    if (!newDomain.trim()) {
      toast.error('Please enter a domain')
      return
    }
    createDomain({ siteId, domain: newDomain.trim(), tlsMode: newTlsMode })
  }

  const handleDeleteDomain = (domainId: string) => {
    if (confirm('Delete this domain?')) {
      deleteDomain({ siteId, domainId })
    }
  }

  const isSavingPrimary = isSettingPrimary || isResetting

  // What the radio group shows: the unsaved local choice, else the database.
  const effectivePrimaryId = selectedPrimaryId ?? currentPrimaryId

  const handleSavePrimaryDomain = () => {
    if (effectivePrimaryId === currentPrimaryId) {
      toast.info('Primary domain is already up to date')
      return
    }
    if (effectivePrimaryId === DEFAULT_SUBDOMAIN_ID) {
      resetPrimaryDomain({ siteId: siteId as string })
    } else {
      setPrimaryDomain({ siteId, domainId: effectivePrimaryId })
    }
  }

  const verifiedDomains = domainList.filter((d) => d.domainVerified)

  // Selectable as primary: verified domains, plus the current one no matter
  // what so the checked radio always has a row to land on.
  const primaryOptionDomains = domainList.filter(
    (d) => d.domainVerified || d.id === currentPrimaryId,
  )

  const defaultSubdomain = site?.slug ? `${site.slug}${SLUG_DOMAIN}` : undefined

  const isBusy = isVerifying || isSettingPrimary || isResetting || isDeleting

  return (
    <ContainerWithSidebarAndNoTopbar>
      <div className="flex flex-col space-y-8">
        <div className="flex flex-col space-y-2">
          <h1 className="text-3xl font-bold">Domain Settings</h1>
          <p className="text-muted-foreground">
            Connect a custom domain to your site. Your site is currently served
            at{' '}
            <span className="font-medium text-foreground">{siteDomain}</span>
            {currentPrimaryId === DEFAULT_SUBDOMAIN_ID &&
              ' (default subdomain)'}
            .
          </p>
        </div>

        {/* Primary domain */}
        <div className="rounded-lg border bg-card p-6 space-y-4">
          <div>
            <h3 className="text-lg font-semibold">Primary Domain</h3>
            <p className="text-sm text-muted-foreground mt-1">
              The primary domain is used to serve your site. Only verified
              domains can be set as primary.
            </p>
          </div>

          {isLoading || isSiteLoading ? (
            <Skeleton className="h-24 w-full max-w-xl rounded-lg" />
          ) : (
            <div
              role="radiogroup"
              aria-label="Primary domain"
              className="space-y-2 max-w-xl"
            >
              {[
                ...(defaultSubdomain
                  ? [
                      {
                        id: DEFAULT_SUBDOMAIN_ID,
                        domain: defaultSubdomain,
                        label: 'Subdomain',
                      },
                    ]
                  : []),
                ...primaryOptionDomains.map((d) => ({
                  id: d.id,
                  domain: d.domain,
                  label: 'Custom domain',
                })),
              ].map((option) => {
                const isSelected = effectivePrimaryId === option.id
                const isCurrent = currentPrimaryId === option.id
                return (
                  <label
                    key={option.id}
                    className={`flex cursor-pointer items-center gap-3 rounded-md border p-3 transition-colors ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'hover:bg-muted/50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="primary-domain"
                      value={option.id}
                      checked={isSelected}
                      onChange={() => setSelectedPrimaryId(option.id)}
                      disabled={isSavingPrimary}
                      className="h-4 w-4 shrink-0 accent-primary"
                    />
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5 sm:flex-row sm:items-center sm:gap-2">
                      <span className="truncate font-mono text-sm">
                        {option.domain}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {option.label}
                      </span>
                    </div>
                    {isCurrent && (
                      <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-green-600">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Current
                      </span>
                    )}
                  </label>
                )
              })}
              {verifiedDomains.length === 0 && (
                <p className="text-xs text-muted-foreground">
                  Add and verify a custom domain below to serve your site from
                  it.
                </p>
              )}
            </div>
          )}

          <div className="flex flex-col items-start space-y-4 rounded-lg border bg-muted/50 p-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
            <div className="flex-1">
              <p className="text-sm font-medium">Save your changes</p>
              <p className="text-sm text-muted-foreground">
                Changes to the primary domain take effect immediately after
                saving.
              </p>
            </div>
            <Button
              onClick={handleSavePrimaryDomain}
              disabled={isSavingPrimary || isLoading || isSiteLoading}
              className="w-full sm:w-auto"
            >
              {isSavingPrimary && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isSavingPrimary ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Custom domains */}
        <div className="rounded-lg border bg-card p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Custom Domains</h3>

            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Domain
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add custom domain</DialogTitle>
                  <DialogDescription>
                    We'll show the DNS records to add at your provider.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="domain">Domain</Label>
                    <Input
                      id="domain"
                      placeholder="blog.example.com"
                      value={newDomain}
                      onChange={(e) => setNewDomain(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
                      autoFocus
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>SSL / TLS</Label>
                    <div className="grid grid-cols-1 gap-2">
                      {(
                        [
                          {
                            value: 'external' as TlsMode,
                            title: 'Free — bring your own SSL',
                            desc: 'Add a CNAME (proxied/orange-cloud is fine) plus a TXT record to verify ownership. You handle TLS (e.g. via your own Cloudflare).',
                          },
                          {
                            value: 'managed' as TlsMode,
                            title: 'Managed SSL (Cloudflare)',
                            desc: 'We issue and renew the certificate automatically. Works on any DNS provider.',
                          },
                        ]
                      ).map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setNewTlsMode(opt.value)}
                          className={`rounded-md border p-3 text-left transition-colors ${
                            newTlsMode === opt.value
                              ? 'border-primary bg-primary/5'
                              : 'hover:bg-muted/50'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-medium">
                              {opt.title}
                            </span>
                            {newTlsMode === opt.value && (
                              <CheckCircle2 className="h-4 w-4 text-primary" />
                            )}
                          </div>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {opt.desc}
                          </p>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setIsAddDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleAddDomain} disabled={isCreating}>
                    {isCreating && (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    )}
                    Add
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {isLoading ? (
            <Skeleton className="h-16 w-full rounded-lg" />
          ) : error ? (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              Failed to load domains
            </div>
          ) : domains?.length === 0 ? (
            <div className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
              No custom domains yet.
            </div>
          ) : (
            <div className="space-y-2">
              {domains?.map((domain: Domain) => {
                const records = domain.verification?.records ?? []
                return (
                  <div key={domain.id} className="rounded-lg border">
                    <div className="flex items-center justify-between gap-3 px-3 py-2.5">
                      <div className="flex min-w-0 items-center gap-2">
                        <span className="truncate text-sm font-medium">
                          {domain.domain}
                        </span>
                        {domain.isPrimary && (
                          <span title="Primary domain">
                            <Star className="h-3.5 w-3.5 fill-primary text-primary" />
                          </span>
                        )}
                        {domain.domainVerified ? (
                          <span className="flex items-center gap-1 text-xs font-medium text-green-600">
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Verified
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs font-medium text-yellow-600">
                            <AlertCircle className="h-3.5 w-3.5" />
                            Pending
                          </span>
                        )}
                        <span className="rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                          {domain.tlsMode === 'managed'
                            ? 'Managed SSL'
                            : 'BYO SSL'}
                        </span>
                      </div>

                      <div className="flex shrink-0 items-center gap-1">
                        {!domain.domainVerified && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              verifyDomain({ siteId, domainId: domain.id })
                            }
                            disabled={isBusy}
                            className="h-7 px-2 text-xs"
                          >
                            {isVerifying ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RefreshCw className="h-3.5 w-3.5" />
                            )}
                            <span className="ml-1.5">Verify</span>
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 w-7 p-0"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                              <span className="sr-only">Open menu</span>
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              variant="destructive"
                              onClick={() => handleDeleteDomain(domain.id)}
                              disabled={isBusy}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>

                    {/* DNS records */}
                    {!domain.domainVerified && records.length > 0 && (
                      <div className="border-t bg-muted/30 px-3 py-2.5">
                        <p className="mb-2 text-xs text-muted-foreground">
                          Add these DNS records, then Verify. Changes can take a
                          few minutes.
                        </p>
                        <div className="overflow-hidden rounded-md border bg-background">
                          <div className="grid grid-cols-12 gap-2 border-b px-2.5 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            <span className="col-span-2">Type</span>
                            <span className="col-span-4">Name</span>
                            <span className="col-span-5">Value</span>
                            <span className="col-span-1 text-right">OK</span>
                          </div>
                          {records.map((record: DnsRecord, i: number) => (
                            <div
                              key={i}
                              className="grid grid-cols-12 items-center gap-2 border-b px-2.5 py-1.5 font-mono text-xs last:border-b-0"
                            >
                              <span className="col-span-2 font-medium">
                                {record.type}
                              </span>
                              <button
                                type="button"
                                onClick={() => handleCopy(record.name)}
                                title={`${record.name} — click to copy`}
                                className="col-span-4 flex items-center gap-1 truncate text-left hover:text-primary"
                              >
                                <span className="truncate">{record.name}</span>
                                <Copy className="h-3 w-3 shrink-0 opacity-50" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleCopy(record.value)}
                                title={`${record.value} — click to copy`}
                                className="col-span-5 flex items-center gap-1 truncate text-left hover:text-primary"
                              >
                                <span className="truncate">{record.value}</span>
                                <Copy className="h-3 w-3 shrink-0 opacity-50" />
                              </button>
                              <span className="col-span-1 flex justify-end">
                                {record.verified === true ? (
                                  <CheckCircle2
                                    className="h-3.5 w-3.5 text-green-600"
                                    aria-label="Found"
                                  />
                                ) : record.verified === false ? (
                                  <AlertCircle
                                    className="h-3.5 w-3.5 text-yellow-600"
                                    aria-label="Not found yet"
                                  />
                                ) : (
                                  <span className="text-muted-foreground">
                                    –
                                  </span>
                                )}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </ContainerWithSidebarAndNoTopbar>
  )
}
